import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Tooltip from "@mui/material/Tooltip";

const ellipsisStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 1,         
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',        
  maxWidth: 180,               
  fontWeight: 500,
  lineHeight: 1.15,           
  cursor: 'pointer',          
};

export function useTypingEffect(fullText: string, speed: number = 10, disabled = false) {
  const [displayed, setDisplayed] = useState("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const indexRef = useRef(0);
  
  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    cleanup(); 
    
    if (disabled || !fullText) {
      setDisplayed(fullText);
      return;
    }
    
    indexRef.current = 0;
    setDisplayed("");
    
    const animate = () => {
      const batchSize = Math.max(1, Math.floor(fullText.length / 50)); 
      let batchCount = 0;
      
      intervalRef.current = setInterval(() => {
        setDisplayed(prev => {
          const newIndex = indexRef.current + 1;
          indexRef.current = newIndex;
          
          if (newIndex >= fullText.length) {
            cleanup();
            return fullText; 
          }
          
          return fullText.slice(0, newIndex);
        });
        
        batchCount++;
        if (indexRef.current >= fullText.length) {
          cleanup();
        }
      }, speed);
    };
    
    animate();
    
    return cleanup; 
  }, [fullText, speed, disabled, cleanup]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return displayed;
}

const TypingTitle: React.FC<{
  title: string;
  animate?: boolean; 
}> = React.memo(({ title, animate = false }) => {
  const typed = useTypingEffect(title, 23, !animate);
  
  const tooltipTitle = useMemo(() => title, [title]);
  
  return (
    <Tooltip title={tooltipTitle} arrow>
      <span style={ellipsisStyle}>{typed}</span>
    </Tooltip>
  );
});

TypingTitle.displayName = 'TypingTitle';

export default TypingTitle;
